import React, { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { Helmet } from 'react-helmet-async';
import UserSidebar from "@/components/UserSidebar";
import BirthDateDialog from "@/components/BirthDateDialog";
import CardRegistrationDialog from "@/components/CardRegistrationDialog";
import PhoneVerificationDialog from "@/components/PhoneVerificationDialog";
import PaymentMethodList, { PaymentMethod } from "@/components/PaymentMethodList";
import { trackPurchase } from "@/utils/customerAnalytics";
import {
  Coins,
  CreditCard,
  Wallet,
  History,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  Clock
} from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/components/AuthContext";
import { toast } from "sonner";
import { fetchAccountTransactions, fetchHomeAddresses, fetchPendingPointPayments, fetchPointCharges, AccountTransaction, PendingPointPayment, PointCharge } from "@/services/customerService";
import { useDemoRestrictions } from "@/hooks/useDemoRestrictions";
import apiClient from "@/lib/apiClient";
import {
  AcceptedPaymentMethods,
  getPaymentMethodMeta,
  usableCustomerPaymentMethods,
} from "@/components/AcceptedPaymentMethods";
import { isGooglePayReady, mountGooglePayButton } from "@/utils/googlePayLoader";

// operation_type の日本語ラベル
const operationTypeLabels: Record<string, string> = {
  'PointConversion': 'PT交換',
  'lottery_draw': 'ガチャ使用',
  'oripa_draw': 'オリパ抽選',
  'kuji_ec_purchase': 'くじ購入',
  'point_charge': 'ポイント購入',
  'refund': '返金',
  'shipping_fee': '送料',
  'Referral': '友だち紹介報酬',
  'CouponRedemption': 'クーポン利用',
};

const getOperationLabel = (operationType: string): string => {
  return operationTypeLabels[operationType] || operationType;
};

interface PointPageProps {
  siteData?: {
    require_phone_verification?: boolean;
    require_birth_date_verification?: boolean;
    point_purchase_enabled?: boolean;
    payment_provider?: 'fincode' | 'gmo_pg';
    gmo_pg_shop_id?: string;
    fincode_shop_id?: string;
    test_mode?: boolean;
    accepted_payment_methods?: string[];
    name?: string;
  };
}

type PointChargePaymentResponse = {
  requires_action?: boolean;
  redirect_url?: string;
  success?: boolean;
  payment_uid?: string;
  amount?: number;
};

type PaymentChannelId = 'card' | 'apple_pay' | 'google_pay' | 'konbini';

type PaymentChannel = {
  id: PaymentChannelId;
  label: string;
  amounts: number[];
  settlement_timing: 'instant' | 'after_payment';
  requires_stored_method: boolean;
  cta_renderer: 'default' | 'apple_pay' | 'google_pay';
};

const POINT_CHARGE_PATH = "/point";
const POINT_CHARGE_COMPLETE_PATH = "/point/complete";

const canUseApplePay = (): boolean => {
  if (typeof window === 'undefined' || !window.isSecureContext) return false;
  if (!window.ApplePaySession || typeof window.ApplePaySession.canMakePayments !== 'function') return false;

  try {
    return window.ApplePaySession.canMakePayments();
  } catch {
    return false;
  }
};

const isSafariBrowser = (): boolean => {
  if (typeof navigator === 'undefined') return false;

  return /Safari/i.test(navigator.userAgent) &&
    !/(Chrome|Chromium|CriOS|Edg|EdgiOS|FxiOS|OPR|OPiOS|Android)/i.test(navigator.userAgent);
};

const isIOSDevice = (): boolean => {
  if (typeof navigator === 'undefined') return false;

  return /iPad|iPhone|iPod/i.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
};

const OripaPointPage: React.FC<PointPageProps> = ({ siteData }) => {
  const location = useLocation();
  const { currentUser, refreshUser } = useAuth();
  const { isDemo } = useDemoRestrictions();
  const [points, setPoints] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedAmount, setSelectedAmount] = useState<number | null>(null);
  const [transactions, setTransactions] = useState<AccountTransaction[]>([]);
  const [pointCharges, setPointCharges] = useState<PointCharge[]>([]);
  const [pendingPointPayments, setPendingPointPayments] = useState<PendingPointPayment[]>([]);
  const [loadingTransactions, setLoadingTransactions] = useState<boolean>(false);
  const [loadingPointCharges, setLoadingPointCharges] = useState<boolean>(false);
  const [expiringSoon, setExpiringSoon] = useState<number>(0);
  const [nextExpiration, setNextExpiration] = useState<{ expires_at: string; amount: number } | null>(null);
  const [showBirthDateDialog, setShowBirthDateDialog] = useState<boolean>(false);
  const [showCardRegistrationDialog, setShowCardRegistrationDialog] = useState<boolean>(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<PaymentMethod | null>(null);
  const [paymentChannels, setPaymentChannels] = useState<PaymentChannel[]>([]);
  const [selectedChannel, setSelectedChannel] = useState<PaymentChannelId | null>(null);
  const [paymentChannelsLoading, setPaymentChannelsLoading] = useState(true);
  const [paymentMethodsRefresh, setPaymentMethodsRefresh] = useState(0);
  const [pendingPurchaseAfterBirthDate, setPendingPurchaseAfterBirthDate] = useState(false);
  const [showPhoneVerificationDialog, setShowPhoneVerificationDialog] = useState(false);
  const [pendingPurchaseAfterPhoneVerification, setPendingPurchaseAfterPhoneVerification] = useState(false);
  const [googlePayLoading, setGooglePayLoading] = useState(false);
  const googlePayButtonRef = useRef<HTMLDivElement | null>(null);
  const googlePayClickHandlerRef = useRef<() => void>(() => {});
  // Apple Pay は pay.joybase.jp のトップレベル checkout に遷移して実行する。
  const [applePayProcessing, setApplePayProcessing] = useState(false);
  const [applePayBrowserAvailable, setApplePayBrowserAvailable] = useState(false);
  // 認証ダイアログ後にどの決済手段で再開するかを記録する。
  const [pendingPurchaseChannel, setPendingPurchaseChannel] = useState<'card' | 'googlepay' | 'applepay' | 'konbini' | null>(null);
  const [konbiniProcessing, setKonbiniProcessing] = useState(false);
  const [konbiniCustomerName, setKonbiniCustomerName] = useState('');
  const konbiniIdempotencyKeyRef = useRef<{ amount: number; key: string } | null>(null);

  const pointPurchaseEnabled = siteData?.point_purchase_enabled !== false;

  // 顧客が実際に選べる決済手段（accepted から準備中・未対応を除外した単一ソース）
  const usableMethods = usableCustomerPaymentMethods(siteData?.accepted_payment_methods ?? []);
  const acceptedCardMethods = usableMethods.filter(
    (method) => getPaymentMethodMeta(method)?.category === 'card'
  );

  // APIの購入可能チャネルを表示・実行可否の単一ソースにする。
  const googlePayEnabled = paymentChannels.some((channel) => channel.id === 'google_pay');
  const applePayEnabled = paymentChannels.some((channel) => channel.id === 'apple_pay');

  const buildPointChargeCompletePath = (paymentUid?: string | null) => {
    if (!paymentUid) return POINT_CHARGE_COMPLETE_PATH;
    return `${POINT_CHARGE_COMPLETE_PATH}?payment_uid=${encodeURIComponent(paymentUid)}`;
  };

  const markPointChargeCompleteUrl = (paymentUid?: string | null) => {
    window.history.replaceState({}, '', buildPointChargeCompletePath(paymentUid));
  };

  const trackPointChargePurchase = (response: PointChargePaymentResponse) => {
    markPointChargeCompleteUrl(response.payment_uid);
    trackPurchase({
      value: Number(response.amount) || 0,
      transactionId: response.payment_uid,
    });
  };

  useEffect(() => {
    setApplePayBrowserAvailable(canUseApplePay());
  }, []);

  useEffect(() => {
    if (!currentUser || !paymentChannels.some((channel) => channel.id === 'konbini')) return;

    let cancelled = false;
    fetchHomeAddresses()
      .then((addresses) => {
        if (cancelled) return;
        const address = addresses.find((candidate) => candidate.default) || addresses[0];
        if (address?.name) setKonbiniCustomerName((current) => current || address.name);
      })
      .catch(() => undefined);

    return () => { cancelled = true; };
  }, [currentUser, paymentChannels]);

  const selectedChannelDefinition = paymentChannels.find((channel) => channel.id === selectedChannel);
  const chargeAmounts = selectedChannelDefinition?.amounts ?? [];

  useEffect(() => {
    if (!currentUser || !pointPurchaseEnabled) return;

    let cancelled = false;
    setPaymentChannelsLoading(true);
    apiClient.get<{ channels: PaymentChannel[] }>('/customer/point_charges/purchase_options')
      .then(async (response) => {
        if (cancelled) return;
        const safariBrowser = isSafariBrowser();
        const googlePayBrowserAvailable = !safariBrowser && !isIOSDevice();
        let channels = response.data.channels.filter((channel) => {
          if (channel.id === 'apple_pay') return safariBrowser && applePayBrowserAvailable;
          if (channel.id === 'google_pay') return googlePayBrowserAvailable;
          return true;
        });

        if (channels.some((channel) => channel.id === 'google_pay')) {
          const googlePayReady = !!siteData?.fincode_shop_id && await isGooglePayReady({
            fincodeShopId: siteData.fincode_shop_id,
            testMode: !!siteData.test_mode,
            merchantName: siteData.name || 'JOYBASE',
          });
          if (cancelled) return;
          channels = channels.filter((channel) => channel.id !== 'google_pay' || googlePayReady);
        }

        setPaymentChannels(channels);
        setSelectedChannel((current) => {
          if (current && channels.some((channel) => channel.id === current)) return current;
          return channels.some((channel) => channel.id === 'card') ? 'card' : null;
        });
      })
      .catch((error) => toast.error(error?.response?.data?.error || '支払い方法を読み込めませんでした'))
      .finally(() => {
        if (!cancelled) setPaymentChannelsLoading(false);
      });

    return () => { cancelled = true; };
  }, [currentUser, pointPurchaseEnabled, applePayBrowserAvailable, siteData?.fincode_shop_id, siteData?.test_mode, siteData?.name]);

  const selectChannel = (channel: PaymentChannel) => {
    setSelectedChannel(channel.id);
    setSelectedAmount((current) => current && channel.amounts.includes(current) ? current : null);
  };

  // Load transactions and point charges
  useEffect(() => {
    const loadData = async () => {
      if (!currentUser) return;

      setLoadingTransactions(true);
      setLoadingPointCharges(true);
      try {
        const [transactionsResponse, chargesData, pendingPaymentsData] = await Promise.all([
          fetchAccountTransactions(),
          fetchPointCharges(),
          fetchPendingPointPayments()
        ]);
        setTransactions(transactionsResponse.transactions);
        setExpiringSoon(transactionsResponse.expiring_soon);
        setNextExpiration(transactionsResponse.next_expiration);
        setPointCharges(chargesData);
        setPendingPointPayments(pendingPaymentsData);
      } catch (error) {
        console.error("Failed to fetch data:", error);
      } finally {
        setLoadingTransactions(false);
        setLoadingPointCharges(false);
      }
    };

    loadData();
  }, [currentUser]);

  // Handle payment status from URL params (redirect from 3DS callback)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const status = params.get("status");
    const paymentUid = params.get("payment_uid");

    if (status === "cancel") {
      toast.error("ポイントの購入がキャンセルされました");
      // URLからstatusパラメータを削除
      window.history.replaceState({}, '', POINT_CHARGE_PATH);
      return;
    }

    // failure または success の場合、payment_uid があればポーリングで詳細確認
    if ((status === "failure" || status === "success") && paymentUid) {
      // URLから処理用 status だけを削除し、成功時は決済識別用 payment_uid を残す
      window.history.replaceState(
        {},
        '',
        status === "success" ? buildPointChargeCompletePath(paymentUid) : POINT_CHARGE_PATH
      );

      // ポーリング開始
      let pollCount = 0;
      const maxPolls = 30; // 最大30回（30秒間）
      const pollInterval = 1000; // 1秒ごと

      const checkPaymentStatus = async () => {
        try {
          const response = await apiClient.get(`/customer/point_charges/status/${paymentUid}`);
          const paymentStatus = response.data.status;

          if (paymentStatus === 'completed') {
            // 決済完了
            toast.success("ポイントの購入が完了しました");
            markPointChargeCompleteUrl(paymentUid);
            // 広告コンバージョン: ポイント購入完了（決済確定 = ポーリング completed 地点で発火）
            trackPurchase({ value: Number(response.data.amount) || 0, transactionId: paymentUid ?? undefined });
            refreshUser();
            fetchAccountTransactions().then(res => {
              setTransactions(res.transactions);
              setExpiringSoon(res.expiring_soon);
              setNextExpiration(res.next_expiration);
            }).catch(console.error);
            fetchPointCharges().then(setPointCharges).catch(console.error);
            return true; // ポーリング終了
          } else if (paymentStatus === 'failed') {
            // 決済失敗
            toast.error("決済処理に失敗しました。カード情報を確認してください。");
            fetchPointCharges().then(setPointCharges).catch(console.error);
            return true; // ポーリング終了
          } else if (paymentStatus === 'cancelled') {
            // 決済キャンセル
            toast.error("決済がキャンセルされました");
            fetchPointCharges().then(setPointCharges).catch(console.error);
            return true; // ポーリング終了
          }

          // pending の場合
          // failure でリダイレクトされたのに pending が続く場合は、一定回数で失敗と判断
          if (status === "failure" && pollCount >= 5) {
            // 5秒経過しても pending なら失敗と判断
            toast.error("決済に失敗しました。カード情報を確認してください。");
            fetchPointCharges().then(setPointCharges).catch(console.error);
            return true; // ポーリング終了
          }

          // まだ pending の場合は継続
          return false;
        } catch (error) {
          console.error("Failed to check payment status:", error);
          // エラーの場合でも、初回リダイレクトのステータスに基づいてメッセージを表示
          if (status === "failure") {
            toast.error("決済に失敗しました。カード情報を確認してください。");
          }
          fetchPointCharges().then(setPointCharges).catch(console.error);
          return true; // エラー時は終了
        }
      };

      const poll = async () => {
        const isDone = await checkPaymentStatus();

        if (isDone) {
          return; // ポーリング終了
        }

        pollCount++;
        if (pollCount < maxPolls) {
          setTimeout(poll, pollInterval);
        } else {
          // タイムアウト
          if (status === "success") {
            toast.info("決済処理を確認しています。しばらくお待ちください。");
            refreshUser();
            fetchAccountTransactions().then(res => {
              setTransactions(res.transactions);
              setExpiringSoon(res.expiring_soon);
              setNextExpiration(res.next_expiration);
            }).catch(console.error);
            fetchPointCharges().then(setPointCharges).catch(console.error);
          } else {
            // failure でタイムアウトした場合は失敗扱い
            toast.error("決済処理に失敗しました");
            fetchPointCharges().then(setPointCharges).catch(console.error);
          }
        }
      };

      // ポーリング開始（少し遅延させる）
      setTimeout(poll, 500);
    } else if (status === "success") {
      // payment_uid がない場合は従来通り（後方互換性）
      toast.success("ポイントの購入が完了しました");
      refreshUser();
      fetchAccountTransactions().then(res => {
        setTransactions(res.transactions);
        setExpiringSoon(res.expiring_soon);
        setNextExpiration(res.next_expiration);
      }).catch(console.error);
      fetchPointCharges().then(setPointCharges).catch(console.error);
      markPointChargeCompleteUrl();
    } else if (status === "failure") {
      // payment_uid がない場合は従来通り
      toast.error("決済に失敗しました。カード情報を確認してください。");
      fetchPointCharges().then(setPointCharges).catch(console.error);
      window.history.replaceState({}, '', POINT_CHARGE_PATH);
    }
  }, []); // 初回レンダリング時のみ実行

  // Update points display when user data changes
  useEffect(() => {
    if (currentUser && currentUser.balance !== undefined) {
      setPoints(currentUser.balance);
    }
  }, [currentUser]);

  // 生年月日登録後に購入処理を自動続行（開始した決済手段で続行）
  useEffect(() => {
    if (pendingPurchaseAfterBirthDate && currentUser?.birth_date_verified) {
      setPendingPurchaseAfterBirthDate(false);
      // 購入処理を実行（モーダルチェックをスキップ）
      if (pendingPurchaseChannel === 'googlepay') {
        executeGooglePayCheckout();
      } else if (pendingPurchaseChannel === 'applepay') {
        executeApplePayCheckout();
      } else if (pendingPurchaseChannel === 'konbini') {
        executeKonbiniCheckout();
      } else {
        executePurchase();
      }
    }
  }, [pendingPurchaseAfterBirthDate, currentUser?.birth_date_verified]);

  // 電話番号認証後に購入処理を自動続行（開始した決済手段で次は生年月日チェックへ）
  useEffect(() => {
    if (pendingPurchaseAfterPhoneVerification && currentUser?.phone_verified) {
      setPendingPurchaseAfterPhoneVerification(false);
      // 生年月日チェックに進む
      if (pendingPurchaseChannel === 'googlepay') {
        handleGooglePayClick();
      } else if (pendingPurchaseChannel === 'applepay') {
        handleApplePayGateClick();
      } else if (pendingPurchaseChannel === 'konbini') {
        handleKonbiniClick();
      } else {
        handlePurchase();
      }
    }
  }, [pendingPurchaseAfterPhoneVerification, currentUser?.phone_verified]);

  // 実際の購入処理を実行（年齢確認済み前提）
  const executePurchase = async () => {
    if (!selectedAmount || !selectedPaymentMethod) {
      return;
    }

    // 18歳未満は購入不可
    if (siteData?.require_birth_date_verification !== false && !currentUser?.is_adult) {
      toast.error("18歳未満の方は利用できません");
      return;
    }

    setLoading(true);
    try {
      const response = await apiClient.post<PointChargePaymentResponse>(
        '/customer/point_charges/with_card',
        {
          payment_method_id: selectedPaymentMethod.id,
          amount: selectedAmount
        }
      );

      if (response.data.requires_action && response.data.redirect_url) {
        // 3Dセキュア認証が必要な場合はリダイレクト
        window.location.href = response.data.redirect_url;
      } else if (response.data.success) {
        // 即座に完了した場合
        toast.success("ポイントの購入が完了しました");
        trackPointChargePurchase(response.data);
        refreshUser();
        fetchAccountTransactions().then(res => {
          setTransactions(res.transactions);
          setExpiringSoon(res.expiring_soon);
          setNextExpiration(res.next_expiration);
        }).catch(console.error);
        fetchPointCharges().then(setPointCharges).catch(console.error);
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || "決済処理に失敗しました";
      toast.error(errorMessage);
      // 失敗時も履歴を更新（失敗履歴が追加されている場合があるため）
      fetchPointCharges().then(setPointCharges).catch(console.error);
    } finally {
      setLoading(false);
    }
  };

  const executeKonbiniCheckout = async () => {
    if (!selectedAmount || konbiniProcessing) return;

    const customerName = konbiniCustomerName.trim();
    if (!customerName) {
      toast.error("お名前を入力してください");
      return;
    }

    if (siteData?.require_birth_date_verification !== false && !currentUser?.is_adult) {
      toast.error("18歳未満の方は利用できません");
      return;
    }

    const currentKey = konbiniIdempotencyKeyRef.current;
    const idempotencyKey = currentKey?.amount === selectedAmount
      ? currentKey.key
      : crypto.randomUUID();
    konbiniIdempotencyKeyRef.current = { amount: selectedAmount, key: idempotencyKey };

    setKonbiniProcessing(true);
    try {
      const response = await apiClient.post<{ payment_url: string }>(
        '/customer/point_charges/with_konbini',
        { amount: selectedAmount, customer_name: customerName },
        { headers: { 'Idempotency-Key': idempotencyKey } }
      );
      window.location.href = response.data.payment_url;
    } catch (error: any) {
      if (error?.response) konbiniIdempotencyKeyRef.current = null;
      toast.error(error?.response?.data?.error || "コンビニ支払いの開始に失敗しました");
      setKonbiniProcessing(false);
    }
  };

  const handleKonbiniClick = () => {
    if (!selectedAmount || konbiniProcessing) return;

    setPendingPurchaseChannel('konbini');

    if (siteData?.require_phone_verification && !currentUser?.phone_verified) {
      setShowPhoneVerificationDialog(true);
      return;
    }

    if (siteData?.require_birth_date_verification !== false && !currentUser?.birth_date_verified) {
      setShowBirthDateDialog(true);
      return;
    }

    executeKonbiniCheckout();
  };

  // Google Pay checkout session を作成し、pay.joybase.jp にトップレベル遷移する。
  const executeGooglePayCheckout = async () => {
    if (!selectedAmount) {
      return;
    }

    if (siteData?.require_birth_date_verification !== false && !currentUser?.is_adult) {
      toast.error("18歳未満の方は利用できません");
      return;
    }

    setGooglePayLoading(true);
    try {
      const response = await apiClient.post<{ checkout_url: string }>(
        '/customer/google_pay/checkout_sessions',
        { amount: selectedAmount }
      );
      window.location.href = response.data.checkout_url;
    } catch (error: any) {
      const errorMessage = error?.response?.data?.error || "Google Pay の開始に失敗しました";
      toast.error(errorMessage);
      setGooglePayLoading(false);
    }
  };

  // Google Pay ボタンクリック時のハンドラ（ボタン onClick に渡す）
  // カード経路の handlePurchase と同じ順序で認証ダイアログを出す（年齢判定はダイアログ通過後）
  const handleGooglePayClick = async () => {
    if (!googlePayEnabled) return;
    if (googlePayLoading) return;

    if (!selectedAmount) {
      toast.error("チャージ額を選択してください");
      return;
    }

    // 認証ダイアログ後に Google Pay 経路で再開するための記録
    setPendingPurchaseChannel('googlepay');

    // 電話番号認証チェック
    if (siteData?.require_phone_verification && !currentUser?.phone_verified) {
      setShowPhoneVerificationDialog(true);
      return;
    }

    // 生年月日認証チェック（年齢判定はこの後 executeGooglePayCheckout 内で実施）
    if (siteData?.require_birth_date_verification !== false && !currentUser?.birth_date_verified) {
      setShowBirthDateDialog(true);
      return;
    }

    executeGooglePayCheckout();
  };

  googlePayClickHandlerRef.current = handleGooglePayClick;

  useEffect(() => {
    const target = googlePayButtonRef.current;

    if (selectedChannel !== 'google_pay' || !googlePayEnabled || !siteData?.fincode_shop_id || !target) {
      if (target) {
        target.replaceChildren();
      }
      return;
    }

    let cancelled = false;
    let cleanup: (() => void) | undefined;

    mountGooglePayButton(
      target,
      {
        fincodeShopId: siteData.fincode_shop_id,
        testMode: !!siteData.test_mode,
        merchantName: siteData.name || 'JOYBASE',
      },
      () => googlePayClickHandlerRef.current()
    )
      .then((cleanupButton) => {
        if (cancelled) {
          cleanupButton();
          return;
        }
        cleanup = cleanupButton;
      })
      .catch((error) => {
        console.error('Failed to mount Google Pay button:', error);
      });

    return () => {
      cancelled = true;
      cleanup?.();
    };
  }, [selectedChannel, googlePayEnabled, siteData?.fincode_shop_id, siteData?.test_mode, siteData?.name]);

  // Apple Pay checkout session を作成し、pay.joybase.jp にトップレベル遷移する。
  const executeApplePayCheckout = async () => {
    if (!selectedAmount) {
      return;
    }

    if (siteData?.require_birth_date_verification !== false && currentUser?.is_adult === false) {
      toast.error('18歳未満の方は利用できません');
      return;
    }

    setApplePayProcessing(true);
    try {
      const response = await apiClient.post<{ checkout_url: string }>(
        '/customer/apple_pay/checkout_sessions',
        { amount: selectedAmount }
      );
      window.location.href = response.data.checkout_url;
    } catch (error: any) {
      const errorMessage = error?.response?.data?.error || 'Apple Pay の開始に失敗しました';
      toast.error(errorMessage);
      setApplePayProcessing(false);
    }
  };

  // Apple Pay ボタンクリック時のハンドラ。カード/Google Pay と同じ順序で認証ダイアログを出す。
  const handleApplePayGateClick = () => {
    if (!selectedAmount) {
      toast.error('チャージ額を選択してください');
      return;
    }
    setPendingPurchaseChannel('applepay');
    if (siteData?.require_phone_verification && !currentUser?.phone_verified) {
      setShowPhoneVerificationDialog(true);
      return;
    }
    if (siteData?.require_birth_date_verification !== false && !currentUser?.birth_date_verified) {
      setShowBirthDateDialog(true);
      return;
    }
    executeApplePayCheckout();
  };

  const handlePurchase = async () => {
    if (!selectedAmount) {
      toast.error("チャージ額を選択してください");
      return;
    }

    if (!selectedPaymentMethod) {
      toast.error("カードを登録してください");
      return;
    }

    // 認証ダイアログ後にカード経路で再開するための記録
    setPendingPurchaseChannel('card');

    // 電話番号認証が必須かつ未完了の場合はモーダルを表示
    if (siteData?.require_phone_verification && !currentUser?.phone_verified) {
      setShowPhoneVerificationDialog(true);
      return;
    }

    // 生年月日認証が必須かつ未登録の場合はモーダルを表示
    if (siteData?.require_birth_date_verification !== false && !currentUser?.birth_date_verified) {
      setShowBirthDateDialog(true);
      return;
    }

    // 年齢確認済みなら購入実行
    executePurchase();
  };

  const handleBirthDateSuccess = async () => {
    setShowBirthDateDialog(false);
    // 購入処理を続行するフラグを設定
    setPendingPurchaseAfterBirthDate(true);
    // ユーザー情報を更新（useEffectで購入処理が続行される）
    await refreshUser();
  };

  const handlePhoneVerificationSuccess = async () => {
    setShowPhoneVerificationDialog(false);
    // 購入処理を続行するフラグを設定
    setPendingPurchaseAfterPhoneVerification(true);
    // ユーザー情報を更新（useEffectで購入処理が続行される）
    await refreshUser();
  };

  const formatNumber = (num: number) => {
    return num.toLocaleString();
  };

  return (
    <>
      <Helmet>
        <title>{pointPurchaseEnabled ? "ポイントチャージ" : "ポイント残高"}</title>
        <meta property="og:title" content={pointPurchaseEnabled ? "ポイントチャージ" : "ポイント残高"} />
      </Helmet>

      <div className="min-h-screen bg-theme-bg">
        <div>
          <div className="max-w-screen-lg mx-auto px-4 pb-4 sm:pb-8 pt-20">
            <div className="flex gap-8">
              {/* サイドバー - デスクトップのみ */}
              <div className="hidden lg:block">
                <UserSidebar />
              </div>

              {/* メインコンテンツ */}
              <section className="flex-1 min-w-0">
                {/* タイトルセクション */}
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-theme-primary border-theme rounded-theme flex items-center justify-center shadow-theme-sm">
                    <Coins className="h-5 w-5 text-theme-button" />
                  </div>
                  <div>
                    <h1 className="text-xl [font-weight:var(--theme-font-weight)] text-theme-bg-text">{pointPurchaseEnabled ? "ポイントチャージ" : "ポイント残高"}</h1>
                    <p className="text-sm [font-weight:var(--theme-font-weight-button)] text-theme-bg-text opacity-70">{pointPurchaseEnabled ? "ポイントを購入してガチャを楽しもう" : "ポイント残高と取引履歴を確認できます"}</p>
                  </div>
                </div>

                {/* コンテンツカード */}
                <div className="p-4 sm:p-6 bg-white border-theme rounded-theme-lg shadow-theme">
        {/* 所有ポイント */}
        <div className="p-4 border-theme rounded-theme bg-gray-100 mb-6">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 bg-theme-primary border-theme rounded-theme flex items-center justify-center">
              <Wallet className="h-4 w-4 text-theme-button" />
            </div>
            <h3 className="text-sm [font-weight:var(--theme-font-weight-button)] text-black">現在のポイント</h3>
          </div>
          <div className="text-3xl [font-weight:var(--theme-font-weight)] text-black">
            {formatNumber(points)}
          </div>
          <p className="text-sm [font-weight:var(--theme-font-weight-button)] text-gray-600 mt-1">ポイント</p>
        </div>

        {/* 期限切れ警告バナー */}
        {expiringSoon > 0 && nextExpiration && (
          <div className="p-4 border-theme rounded-theme bg-yellow-50 border-yellow-200 mb-6">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-yellow-400 border-theme rounded-theme flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="h-4 w-4 text-black" />
              </div>
              <div>
                <h3 className="text-sm [font-weight:var(--theme-font-weight)] text-black">ポイントの有効期限が近づいています</h3>
                <p className="text-sm [font-weight:var(--theme-font-weight-button)] text-gray-700 mt-1">
                  {formatNumber(expiringSoon)}ポイントが30日以内に期限切れになります。
                  次の期限: {new Date(nextExpiration.expires_at).toLocaleDateString('ja-JP')}
                </p>
              </div>
            </div>
          </div>
        )}

        {pointPurchaseEnabled && (
        <div className="max-w-3xl mx-auto space-y-8">
          <fieldset>
            <legend className="flex items-center gap-2 mb-3 text-sm [font-weight:var(--theme-font-weight-button)] text-black">
              <span className="w-8 h-8 bg-theme-primary border-theme rounded-theme flex items-center justify-center">1</span>
              支払い方法を選択
            </legend>
            {paymentChannelsLoading ? (
              <div className="h-24 bg-gray-100 animate-pulse rounded-theme" aria-busy="true" />
            ) : (
              <div className="grid sm:grid-cols-2 gap-2">
                {paymentChannels.map((channel) => (
                  <label
                    key={channel.id}
                    className={`flex items-center gap-3 min-h-12 px-4 py-3 border-theme rounded-theme cursor-pointer ${selectedChannel === channel.id ? 'bg-theme-primary' : 'bg-white hover:bg-gray-50'}`}
                  >
                    <input
                      type="radio"
                      name="payment-channel"
                      value={channel.id}
                      checked={selectedChannel === channel.id}
                      onChange={() => selectChannel(channel)}
                    />
                    <span className={`[font-weight:var(--theme-font-weight)] ${selectedChannel === channel.id ? 'text-theme-button' : 'text-black'}`}>{channel.label}</span>
                  </label>
                ))}
              </div>
            )}
          </fieldset>

          {selectedChannel === 'card' && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <CreditCard className="h-5 w-5" />
                <h3 className="text-sm [font-weight:var(--theme-font-weight-button)] text-black">使用するカード</h3>
              </div>
              {acceptedCardMethods.length > 0 && (
                <AcceptedPaymentMethods methods={acceptedCardMethods} size="sm" className="mb-3" />
              )}
              <PaymentMethodList
                onAddCard={() => setShowCardRegistrationDialog(true)}
                onSelect={setSelectedPaymentMethod}
                refreshTrigger={paymentMethodsRefresh}
              />
            </div>
          )}

          <fieldset disabled={!selectedChannel}>
            <legend className="flex items-center gap-2 mb-3 text-sm [font-weight:var(--theme-font-weight-button)] text-black">
              <span className="w-8 h-8 bg-theme-primary border-theme rounded-theme flex items-center justify-center">2</span>
              チャージ額を選択
            </legend>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6">
                {chargeAmounts.map((amount) => (
                  <button
                    key={amount}
                    type="button"
                    className={`p-4 border-theme rounded-theme transition-all text-center ${
                      selectedAmount === amount
                        ? 'bg-theme-primary shadow-theme-sm'
                        : 'bg-white hover:bg-gray-100'
                    }`}
                    onClick={() => setSelectedAmount(amount)}
                  >
                    <div className={`text-lg [font-weight:var(--theme-font-weight)] ${
                      selectedAmount === amount ? 'text-theme-button' : 'text-black'
                    }`}>
                      ¥{formatNumber(amount)}
                    </div>
                    <div className={`text-xs [font-weight:var(--theme-font-weight-button)] mt-1 ${
                      selectedAmount === amount ? 'text-theme-button opacity-80' : 'text-gray-600'
                    }`}>
                      {formatNumber(amount)}P
                    </div>
                  </button>
                ))}
              </div>
          </fieldset>

          {selectedChannelDefinition && selectedAmount && (
            <div className="border-y border-gray-200 py-4 text-sm space-y-2">
              <div className="flex justify-between"><span className="text-gray-600">支払い方法</span><span>{selectedChannelDefinition.label}</span></div>
              <div className="flex justify-between"><span className="text-gray-600">支払い金額</span><span>¥{formatNumber(selectedAmount)}</span></div>
              <div className="flex justify-between"><span className="text-gray-600">付与予定</span><span>{formatNumber(selectedAmount)}ポイント</span></div>
            </div>
          )}

          {selectedChannel === 'card' && (
              <button
                className="w-full h-12 px-4 bg-theme-primary text-theme-button border-theme rounded-theme [font-weight:var(--theme-font-weight)] shadow-theme hover:shadow-theme-hover hover:translate-x-[2px] hover:translate-y-[2px] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={handlePurchase}
                disabled={!selectedAmount || !selectedPaymentMethod || loading}
              >
                {loading ? (
                  <div className="flex items-center justify-center gap-2">
                    <div className="w-5 h-5 border-theme border-t-transparent rounded-full animate-spin" />
                    処理中...
                  </div>
                ) : selectedAmount ? (
                  `¥${formatNumber(selectedAmount)} 購入する`
                ) : (
                  '金額を選択してください'
                )}
              </button>
          )}

              {selectedChannel === 'google_pay' && googlePayEnabled && (
                <div>
                  <div
                    ref={googlePayButtonRef}
                    className={`w-full min-h-[48px] transition-opacity ${
                      selectedAmount && !googlePayLoading
                        ? ''
                        : 'opacity-50 pointer-events-none'
                    }`}
                  />
                </div>
              )}

              {selectedChannel === 'apple_pay' && applePayEnabled && applePayBrowserAvailable && (
                <div>
                  <button
                    type="button"
                    className="w-full h-[48px] rounded-theme [-webkit-appearance:-apple-pay-button] [-apple-pay-button-type:buy] [-apple-pay-button-style:black] disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={!selectedAmount || applePayProcessing}
                    aria-label={selectedAmount ? 'Apple Payで購入' : 'Apple Pay'}
                    onClick={handleApplePayGateClick}
                  />
                </div>
              )}

              {selectedChannel === 'konbini' && (
                <div className="space-y-3">
                  <div className="border border-gray-300 bg-gray-50 p-3 text-sm text-gray-700">
                    <p>申込み後に表示されるバーコードを対応するコンビニの店頭で提示してください。</p>
                    <p className="mt-1">支払期限は申込みから3日以内です。ポイントは店頭での支払い確認後に反映されます。</p>
                  </div>
                  <div>
                    <label htmlFor="konbini-customer-name" className="mb-1 block text-sm font-medium text-gray-700">
                      お名前
                    </label>
                    <input
                      id="konbini-customer-name"
                      type="text"
                      value={konbiniCustomerName}
                      onChange={(event) => setKonbiniCustomerName(event.target.value)}
                      maxLength={50}
                      autoComplete="name"
                      className="h-12 w-full rounded-theme border border-gray-300 bg-white px-3 text-base text-gray-900 focus:border-theme-primary focus:outline-none focus:ring-2 focus:ring-theme-primary/20"
                      placeholder="山田 太郎"
                    />
                  </div>
                  <button
                    type="button"
                    className="w-full h-12 px-4 bg-theme-primary text-theme-button border-theme rounded-theme [font-weight:var(--theme-font-weight)] shadow-theme hover:shadow-theme-hover hover:translate-x-[2px] hover:translate-y-[2px] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={handleKonbiniClick}
                    disabled={!selectedAmount || !konbiniCustomerName.trim() || konbiniProcessing}
                  >
                    {konbiniProcessing
                      ? '処理中...'
                      : selectedAmount
                        ? `¥${formatNumber(selectedAmount)} の支払い案内へ進む`
                        : '金額を選択してください'}
                  </button>
                </div>
              )}

              <p className="text-xs font-medium text-gray-500 mt-4 text-center">
                SSL暗号化通信により安全に保護されます
              </p>
        </div>
        )}

        {pendingPointPayments.length > 0 && (
          <section className="mt-8" aria-labelledby="pending-konbini-heading">
            <div className="flex items-center gap-2 mb-3">
              <Clock className="h-5 w-5 text-gray-600" />
              <h2 id="pending-konbini-heading" className="text-base [font-weight:var(--theme-font-weight)] text-black">
                コンビニ支払い待ち
              </h2>
            </div>
            <div className="border-theme rounded-theme divide-y divide-[var(--theme-border-color)] bg-white">
              {pendingPointPayments.map((payment) => (
                <div key={payment.payment_uid} className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <p className="[font-weight:var(--theme-font-weight-button)] text-black">
                      ¥{formatNumber(payment.amount)} <span className="ml-2 text-sm text-amber-700">{payment.status_label}</span>
                    </p>
                    <p className="mt-1 text-xs text-gray-500">
                      {payment.expires_at
                        ? `支払期限: ${new Date(payment.expires_at).toLocaleString('ja-JP')}`
                        : `申込日時: ${new Date(payment.created_at).toLocaleString('ja-JP')}`}
                    </p>
                    <p className="mt-1 text-xs text-gray-500">支払い確認後にポイントが反映されます。</p>
                  </div>
                  <p className="max-w-sm text-xs leading-relaxed text-gray-600 sm:text-right">
                    {payment.status === 'awaiting_payment'
                      ? 'fincodeから登録メールアドレスに届いたバーコード案内をご確認ください。'
                      : '支払い案内を準備しています。しばらくしてから再度ご確認ください。'}
                  </p>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* 履歴タブ */}
        <div className="mt-8">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 bg-theme-primary border-theme rounded-theme flex items-center justify-center">
              <History className="h-4 w-4 text-theme-button" />
            </div>
            <h2 className="text-lg [font-weight:var(--theme-font-weight)] text-black">履歴</h2>
          </div>

          <Tabs defaultValue={pointPurchaseEnabled ? "purchases" : "transactions"} className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-4 bg-gray-100 border-theme rounded-theme p-1">
              {pointPurchaseEnabled && (
                <TabsTrigger value="purchases" className="[font-weight:var(--theme-font-weight-button)] data-[state=active]:bg-theme-primary data-[state=active]:text-theme-button rounded-md">購入履歴</TabsTrigger>
              )}
              <TabsTrigger value="transactions" className={`[font-weight:var(--theme-font-weight-button)] data-[state=active]:bg-theme-primary data-[state=active]:text-theme-button rounded-md ${!pointPurchaseEnabled ? 'col-span-2' : ''}`}>{pointPurchaseEnabled ? "ポイント利用履歴" : "取引履歴"}</TabsTrigger>
            </TabsList>

            {/* ポイント利用履歴タブ */}
            <TabsContent value="transactions">
              <div className="border-theme rounded-theme overflow-hidden">
                {loadingTransactions ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="w-6 h-6 border-theme border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : transactions.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="[font-weight:var(--theme-font-weight-button)] text-gray-600">履歴がありません</p>
                  </div>
                ) : (
                  <div className="divide-y divide-[var(--theme-border-color)]">
                    {transactions.map((transaction) => {
                      const isPositive = transaction.amount > 0;
                      const isExpired = transaction.expired;
                      return (
                        <div
                          key={transaction.id}
                          className={`flex items-center justify-between p-4 hover:bg-gray-50 transition-colors ${isExpired ? 'bg-gray-50' : 'bg-white'}`}
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-theme border-theme flex items-center justify-center ${
                              isExpired ? 'bg-gray-300' : isPositive ? 'bg-green-400' : 'bg-red-400'
                            }`}>
                              {isPositive ? (
                                <TrendingUp className="h-5 w-5 text-black" />
                              ) : (
                                <TrendingDown className="h-5 w-5 text-black" />
                              )}
                            </div>
                            <div>
                              <div className={`[font-weight:var(--theme-font-weight-button)] ${isExpired ? 'text-gray-400' : 'text-black'}`}>
                                {transaction.operation?.description || getOperationLabel(transaction.operation_type)}
                                {isExpired && <span className="ml-2 text-red-500 text-xs">(期限切れ)</span>}
                              </div>
                              {transaction.operation?.event_name && (
                                <div className={`text-sm font-medium ${isExpired ? 'text-gray-400' : 'text-gray-600'}`}>
                                  {transaction.operation.event_name}
                                </div>
                              )}
                              <div className="text-xs font-medium text-gray-500">
                                {new Date(transaction.created_at).toLocaleString('ja-JP')}
                              </div>
                              {/* 有効期限表示（入金取引のみ） */}
                              {isPositive && transaction.expires_at && (
                                <div className={`flex items-center gap-1 text-xs font-medium mt-1 ${
                                  isExpired ? 'text-red-400' : 'text-gray-500'
                                }`}>
                                  <Clock className="h-3 w-3" />
                                  有効期限: {new Date(transaction.expires_at).toLocaleDateString('ja-JP')}
                                  {!isExpired && transaction.remaining_amount !== null && (
                                    <span className="ml-2">
                                      (残: {formatNumber(transaction.remaining_amount)}{transaction.currency})
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className={`text-lg [font-weight:var(--theme-font-weight)] ${
                            isExpired ? 'text-gray-400 line-through' : isPositive ? 'text-green-600' : 'text-red-600'
                          }`}>
                            {isPositive ? '+' : ''}{formatNumber(transaction.amount)} {transaction.currency}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </TabsContent>

            {/* 購入履歴タブ */}
            <TabsContent value="purchases">
              <div className="border-theme rounded-theme overflow-hidden">
                {loadingPointCharges ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="w-6 h-6 border-theme border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : pointCharges.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="[font-weight:var(--theme-font-weight-button)] text-gray-600">購入履歴がありません</p>
                  </div>
                ) : (
                  <div className="divide-y divide-[var(--theme-border-color)]">
                    {pointCharges.map((charge) => {
                      const isCompleted = charge.status === 'completed';
                      return (
                        <div
                          key={charge.id}
                          className="flex items-center justify-between p-4 bg-white hover:bg-gray-50 transition-colors"
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-theme border-theme flex items-center justify-center ${isCompleted ? 'bg-green-400' : 'bg-red-400'}`}>
                              <CreditCard className="h-5 w-5 text-black" />
                            </div>
                            <div>
                              <div className="[font-weight:var(--theme-font-weight-button)] text-black">
                                ポイント購入
                              </div>
                              <div className="text-xs font-medium text-gray-500">
                                {new Date(charge.created_at).toLocaleString('ja-JP')}
                              </div>
                              {charge.payment_uid && (
                                <div className="text-[10px] text-gray-400 break-all">
                                  ID: {charge.payment_uid}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-lg [font-weight:var(--theme-font-weight)] text-black">
                              ¥{formatNumber(charge.amount)}
                            </div>
                            <span className={`inline-block px-2 py-0.5 text-xs [font-weight:var(--theme-font-weight-button)] rounded-theme border-theme ${isCompleted ? 'bg-green-400' : 'bg-red-400'}`}>
                              {charge.status_label}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </div>
                </div>
              </section>
            </div>
          </div>
        </div>
      </div>

      {/* 生年月日入力モーダル */}
      <BirthDateDialog
        isOpen={showBirthDateDialog}
        onClose={() => setShowBirthDateDialog(false)}
        onSuccess={handleBirthDateSuccess}
      />

      {/* カード登録モーダル */}
      <CardRegistrationDialog
        isOpen={showCardRegistrationDialog}
        onClose={() => setShowCardRegistrationDialog(false)}
        onSuccess={() => setPaymentMethodsRefresh(prev => prev + 1)}
        paymentProvider={siteData?.payment_provider}
        gmoPgShopId={siteData?.gmo_pg_shop_id}
        testMode={siteData?.test_mode}
      />

      {/* 電話番号認証モーダル */}
      <PhoneVerificationDialog
        isOpen={showPhoneVerificationDialog}
        onClose={() => setShowPhoneVerificationDialog(false)}
        onSuccess={handlePhoneVerificationSuccess}
      />
    </>
  );
};

export default OripaPointPage;
